import React, { useState } from "react";
import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";

import { Button, Card, FilterGroups, FilterValues, PageHeader, Table, FilterTypes } from "@cofano/cck";
import { Box, Tooltip } from "@mui/material";

import Helmet from "components/helmet/Helmet";
import CountrySelect, { countries } from "components/selects/CountrySelect";
import { TableId, useBackendTable } from "components/table";

import LocationSelect, { LocationSelectOption } from "view/locations/components/selects/LocationSelect";
import ProductSelect, { ProductSelectOption } from "view/products/components/select/ProductSelect";
import RelationSelect, { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import CharterSubtypeTag from "view/charters/components/tags/CharterSubtypeTag";

import RechargeDownloadButton from "./components/download/RechargeDownloadButton";
import RechargeBillingCommentStatus from "./components/status/RechargeBillingCommentStatus";
import RechargeBillingStateStatus from "./components/status/RechargeBillingStateStatus";
import RechargeBillingStateSelect from "./components/selects/RechargeBillingStateSelect";
import RechargeBillingCommentSelect from "./components/selects/RechargeBillingCommentSelect";
import RechargeBillingStatusEditDialog from "./components/dialogs/RechargeBillingStatusEditDialog";

import { DataFormat, Language, usePermission } from "utils";
import { cargoActionBLComparison } from "utils/cargoActionUtils";
import {
	CargoNominationNumberLink,
	LocationLink,
	ProductLink,
	RelationLink,
	ShipLink,
	VoyageLink,
} from "utils/LinkUtils";
import { FormattedDate, FormattedDateWindow } from "utils/DateUtils";

import { CallType, LocationType, RechargeBillingComment, RechargeBillingState, SortDirection } from "__generated__/enums";
import { GetRechargeLinesDocument, GetRechargeLinesQuery } from "__generated__/view/recharge/graphql/recharge.queries.generated";
import { ChildPropsFromPagination } from "types/commons";

export type RechargeLineFromQuery = ChildPropsFromPagination<GetRechargeLinesQuery, "cargoActions">;

const Recharge = () => {
	const client = useApolloClient();

	const { isReadOnly } = usePermission();

	const [ dialog, setDialog ] = useState<{ open: boolean; cargoActionIds: number[] }>({ open: false, cargoActionIds: [] });

	const { tableProps, filterProps, filterValuesProps, refetch } = useBackendTable({
		id: TableId.Recharge,
		selectSettings: {
			isFullSelectEnabled: false,
		},
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetRechargeLinesDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "id",
								direction: SortDirection.Desc,
							} ],
						},
						search: {
							... filter,
							blDate: {
								from: filter.blDate ? (filter.blDate as [number | null, number | null])[0] : undefined,
								to: filter.blDate ? (filter.blDate as [number | null, number | null])[1] : undefined,
							},
							nominationDate: {
								from: filter.nominationDate ? (filter.nominationDate as [number | null, number | null])[0] : undefined,
								to: filter.nominationDate ? (filter.nominationDate as [number | null, number | null])[1] : undefined,
							},
							loadingLocations: filter.loadingLocations?.map((it: LocationSelectOption) => it.id),
							dischargingLocations: filter.dischargingLocations?.map((it: LocationSelectOption) => it.id),
							loadingPorts: filter.loadingPorts?.map((it: LocationSelectOption) => it.id),
							dischargingPorts: filter.dischargingPorts?.map((it: LocationSelectOption) => it.id),
							products: filter.products?.map((it: ProductSelectOption) => it.id),
							consignees: filter.consignees?.map((it: RelationSelectOption) => it.id),
							consignors: filter.consignors?.map((it: RelationSelectOption) => it.id),
							type: CallType.Discharging,
							hasCall: true,
							loadingCountries: filter.loadingCountries?.map((it: string) => it),
							dischargingCountries: filter.dischargingCountries?.map((it: string) => it),
						},
					},
				});

				callback({
					content: result.data.cargoActions.content,
					totalResults: result.data.cargoActions.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	return (
		<React.Fragment>
			<PageHeader>
				<PageHeader.Title>
					{Language("RECHARGE")}
					<Helmet title={intl.formatMessage({ id: "RECHARGE" })} />
				</PageHeader.Title>
			</PageHeader>

			{filterValuesProps.values.length > 0 && (
				<Box mb={2}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={[ {
								id: "blDate",
								title: intl.formatMessage({ id: "BL_TIME" }),
								renderFilterValue: (value: [number, number]) => <FormattedDateWindow from={value[0]} to={value[1]} format="ll" />,
							}, {
								id: "nominationDate",
								title: intl.formatMessage({ id: "NOMINATED_LOAD_DATE" }),
								renderFilterValue: (value: [number, number]) => <FormattedDateWindow from={value[0]} to={value[1]} format="ll" />,
							}, {
								id: "loadingCountries",
								title: intl.formatMessage({ id: "ORDER_LOADING_COUNTRY" }),
								renderFilterValue: (value: string[]) => {
									return value.map((code) => countries.find((c) => c.code === code)?.name ?? code).join(", ");
								},
							}, {
								id: "dischargingCountries",
								title: intl.formatMessage({ id: "ORDER_DISCHARGING_COUNTRY" }),
								renderFilterValue: (value: string[]) => {
									return value.map((code) => countries.find((c) => c.code === code)?.name ?? code).join(", ");
								},
							}, {
								id: "loadingPorts",
								title: intl.formatMessage({ id: "ORDER_LOADING_PORT" }),
								renderFilterValue: (value: LocationSelectOption[]) => value.map((it) => it.description).join(", "),
							}, {
								id: "dischargingPorts",
								title: intl.formatMessage({ id: "ORDER_DISCHARGING_PORT" }),
								renderFilterValue: (value: LocationSelectOption[]) => value.map((it) => it.description).join(", "),
							}, {
								id: "loadingLocations",
								title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
								renderFilterValue: (value: LocationSelectOption[]) => value.map((it) => it.description).join(", "),
							}, {
								id: "dischargingLocations",
								title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
								renderFilterValue: (value: LocationSelectOption[]) => value.map((it) => it.description).join(", "),
							}, {
								id: "products",
								title: intl.formatMessage({ id: "PRODUCT" }),
								renderFilterValue: (value: ProductSelectOption[]) =>  value.map((it) => it.name).join(", "),
							}, {
								id: "consignees",
								title: intl.formatMessage({ id: "ORDER_CONSIGNEE" }),
								renderFilterValue: (value: RelationSelectOption[]) =>  value.map((it) => it.name).join(", "),
							}, {
								id: "consignors",
								title: intl.formatMessage({ id: "ORDER_CONSIGNOR" }),
								renderFilterValue: (value: RelationSelectOption[]) =>  value.map((it) => it.name).join(", "),
							}, {
								id: "billingComment",
								title: intl.formatMessage({ id: "RECHARGE_COMMENT" }),
								renderFilterValue: (value: RechargeBillingComment) => intl.formatMessage({ id: `RECHARGE_COMMENT_${value}` }),
							}, {
								id: "billingState",
								title: intl.formatMessage({ id: "RECHARGE_BILLING_STATE" }),
								renderFilterValue: (value: RechargeBillingState) =>  intl.formatMessage({ id: `RECHARGE_BILLING_STATE_${value}` }),
							} ]} />
					</Card>
				</Box>
			)}

			<Card disablePadding>
				<Table
					actions={!isReadOnly ? [ {
						render: (selection: RechargeLineFromQuery[] | null, isFullSelect: boolean) => {
							const cargoActionIds = (selection ?? []).map(it => it.id);
							const disabled = (cargoActionIds.length === 0) || (cargoActionIds.length > 200) || (isFullSelect && tableProps.paginationSettings.totalResults > 200);
							return (
								<Tooltip title={disabled ? intl.formatMessage({ id: "PLEASE_SELECT_LESS_THAN_200_RECHARGE_LINES" }) : undefined}>
									<span>
										<Button
											onClick={() => setDialog({ open: true, cargoActionIds: cargoActionIds })}
											disabled={disabled}
											variant="outlined"
											size="small">
											{intl.formatMessage({ id: "RECHARGE_EDIT_BILLING_STATE" })}
										</Button>
									</span>
								</Tooltip>
							);
						},
					}, {
						render: (selection, isFullSelect, _event, state) => {
							const cargoActionIds = (selection ?? []).map(it => it.id);
							return (
								<RechargeDownloadButton
									cargoActionIds={cargoActionIds}
									columnIds={state.currentColumns.map((c: { column: { id: string; }; }) => c.column.id)}
									disabled={(cargoActionIds.length === 0) || (cargoActionIds.length > 200) || (isFullSelect && tableProps.paginationSettings.totalResults > 200)} />
							);
						},
					} ] : undefined}
					filterComponent={() => <FilterGroups {...filterProps}
						fields={[ {
							id: "blDate",
							title: intl.formatMessage({ id: "BL_TIME" }),
							filterType: FilterTypes.DATE_RANGE,
						}, {
							id: "nominationDate",
							title: intl.formatMessage({ id: "NOMINATED_LOAD_DATE" }),
							filterType: FilterTypes.DATE_RANGE,
						}, {
							id: "loadingCountries",
							title: intl.formatMessage({ id: "ORDER_LOADING_COUNTRY" }),
							render: (value, onChange) => <CountrySelect value={value} onChange={onChange} multiple />,
						}, {
							id: "dischargingCountries",
							title: intl.formatMessage({ id: "ORDER_DISCHARGING_COUNTRY" }),
							render: (value, onChange) => <CountrySelect value={value} onChange={onChange} multiple	/>,
						}, {
							id: "loadingPorts",
							title: intl.formatMessage({ id: "ORDER_LOADING_PORT" }),
							render: (value, onChange) => (
								<LocationSelect
									types={[ LocationType.Port ]}
									value={value}
									onChange={onChange}
									multiple />
							),
						}, {
							id: "dischargingPorts",
							title: intl.formatMessage({ id: "ORDER_DISCHARGING_PORT" }),
							render: (value, onChange) =>
								<LocationSelect
									types={[ LocationType.Port ]}
									value={value}
									onChange={onChange}
									multiple
								/>,
						}, {
							id: "loadingLocations",
							title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
							render: (value, onChange) => <LocationSelect value={value} onChange={onChange} multiple />,
						}, {
							id: "dischargingLocations",
							title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
							render: (value, onChange) => <LocationSelect value={value} onChange={onChange} multiple />,
						}, {
							id: "products",
							title: intl.formatMessage({ id: "PRODUCT" }),
							render: (value, onChange) => ( <ProductSelect value={value} onChange={onChange} multiple />),
						}, {
							id: "consignees",
							title: intl.formatMessage({ id: "ORDER_CONSIGNEE" }),
							render: (value, onChange) => <RelationSelect value={value} onChange={onChange} multiple />,
						}, {
							id: "consignors",
							title: intl.formatMessage({ id: "ORDER_CONSIGNOR" }),
							render: (value, onChange) => <RelationSelect value={value} onChange={onChange} multiple />,
						}, {
							id: "billingComment",
							title: intl.formatMessage({ id: "RECHARGE_COMMENT" }),
							render: (value, onChange) =>  <RechargeBillingCommentSelect value={value} onChange={onChange} />,
						}, {
							id: "billingState",
							title: intl.formatMessage({ id: "RECHARGE_BILLING_STATE" }),
							render: (value, onChange) =>  <RechargeBillingStateSelect value={value} onChange={onChange} />,
						} ]} /> }
					{...tableProps}
					columns={[
						{
							id: "cargo.product",
							title: intl.formatMessage({ id: "PRODUCT" }),
							render: (row: RechargeLineFromQuery) => <ProductLink product={row.cargo.product} />,
						}, {
							id: "weight",
							title: intl.formatMessage({ id: "WEIGHT" }),
							render: (row: RechargeLineFromQuery) => DataFormat.tonnage(row.weight),
						}, {
							id: "volume",
							title: intl.formatMessage({ id: "CARGO_VOLUME" }),
							render: (row: RechargeLineFromQuery) => DataFormat.volumeM3(row.volume),
						}, {
							id: "firstCorrespondingLoadingAction.call.atd",
							title: intl.formatMessage({ id: "BL_TIME" }),
							render: (row: RechargeLineFromQuery) => <FormattedDate date={row.firstCorrespondingLoadingAction?.call?.atd} withoutYear />,
						}, {
							id: "firstCorrespondingLoadingAction.call.pta",
							title: intl.formatMessage({ id: "NOMINATED_LOAD_DATE" }),
							render: (row: RechargeLineFromQuery) => <FormattedDate date={row.firstCorrespondingLoadingAction?.call?.pta} withoutYear />,
						}, {
							id: "call.voyage",
							title: intl.formatMessage({ id: "VOYAGE" }),
							render: (row: RechargeLineFromQuery) => <VoyageLink voyage={row.call?.voyage} />,
						}, {
							id: "call.voyage.ship",
							title: intl.formatMessage({ id: "SHIP" }),
							render: (row: RechargeLineFromQuery) => <ShipLink ship={row.call?.voyage.ship} />,
						}, {
							id: "call.voyage.ship.owner",
							title: intl.formatMessage({ id: "RELATION_SHIP_OWNER" }),
							render: (row: RechargeLineFromQuery) => <RelationLink relation={row.call?.voyage.ship.owner} />,
						}, {
							id: "call.voyage.ship.operator",
							title: intl.formatMessage({ id: "OPERATOR" }),
							render: (row: RechargeLineFromQuery) => <RelationLink relation={row.call?.voyage.ship.operator} />,
						}, {
							id: "call.voyage.charter.subtype",
							title: intl.formatMessage({ id: "CHARTER_TYPE" }),
							render: (row: RechargeLineFromQuery) => row.call?.voyage.purchaseCharters?.[0]?.subtype && <CharterSubtypeTag type={row.call.voyage.purchaseCharters[0].subtype} />,
						}, {
							id: "location",
							title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
							render: (row: RechargeLineFromQuery) => <LocationLink location={row.location} />,
						}, {
							id: "firstCorrespondingLoadingAction.location",
							title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
							render: (row: RechargeLineFromQuery) => <LocationLink location={row.firstCorrespondingLoadingAction?.location} />,
						}, {
							id: "cargo.supplier",
							title: intl.formatMessage({ id: "SUPPLIER" }),
							render: (row: RechargeLineFromQuery) => <RelationLink relation={row.cargo.supplier} />,
						}, {
							id: "cargo.consignor",
							title: intl.formatMessage({ id: "CARGO_CONSIGNOR" }),
							render: (row: RechargeLineFromQuery) => <RelationLink relation={row.cargo.consignor} />,
						}, {
							id: "cargo.consignee",
							title: intl.formatMessage({ id: "ORDER_CONSIGNEE" }),
							render: (row: RechargeLineFromQuery) => <RelationLink relation={row.cargo.consignee} />,
						}, {
							id: "cargo.nominationNumber",
							title: intl.formatMessage({ id: "NOMINATION_NUMBER" }),
							render: (row: RechargeLineFromQuery) => <CargoNominationNumberLink cargo={{ id: row.cargo.id, nominationNumber: row.cargo.nominationNumber || "" }} />,
						}, {
							id: "cargo.measurements.locationWeight",
							title: intl.formatMessage({ id: "AGREEMENT_TYPE_BL_WEIGHT" }),
							render: (row: RechargeLineFromQuery) => cargoActionBLComparison(row, "WEIGHT"),
						}, {
							id: "cargo.measurements.locationVolume",
							title: intl.formatMessage({ id: "AGREEMENT_TYPE_BL_VOLUME" }),
							render: (row: RechargeLineFromQuery) => cargoActionBLComparison(row, "VOLUME"),
						}, {
							id: "financialInfo.freightCost",
							title: intl.formatMessage({ id: "RECHARGE_FREIGHT_COST" }),
							render: (row: RechargeLineFromQuery) => formatLumpsumAndPricePerMT(row.financialInfo?.lumpsum.freightCost, row.financialInfo?.perMetricTon.freightCost, row.cargo?.charter?.contract?.currency?.isoCode ?? undefined),
						}, {
							id: "financialInfo.surcharge",
							title: intl.formatMessage({ id: "RECHARGE_SURCHARGE" }),
							render: (row: RechargeLineFromQuery) => formatLumpsumAndPricePerMT(row.financialInfo?.lumpsum.surcharge, row.financialInfo?.perMetricTon.surcharge, row.cargo?.charter?.contract?.currency?.isoCode ?? undefined),
						}, {
							id: "financialInfo.price",
							title: intl.formatMessage({ id: "RECHARGE_PRICE" }),
							render: (row: RechargeLineFromQuery) => formatLumpsumAndPricePerMT(row.financialInfo?.lumpsum.price, row.financialInfo?.perMetricTon.price, row.cargo?.charter?.contract?.currency?.isoCode ?? undefined),
						}, {
							id: "financialInfo.subtotal",
							title: intl.formatMessage({ id: "RECHARGE_SUBTOTAL" }),
							render: (row: RechargeLineFromQuery) => row.financialInfo?.subtotal ? DataFormat.price(row.financialInfo.subtotal, row.cargo?.charter?.contract?.currency?.isoCode ?? undefined) : undefined,
						}, {
							id: "financialInfo.bunkerSurchargePercentage",
							title: intl.formatMessage({ id: "RECHARGE_BUNKER_SURCHARGE_PERCENTAGE" }),
							render: (row: RechargeLineFromQuery) => row.financialInfo?.bunkerSurchargePercentage ? DataFormat.percentage(row.financialInfo.bunkerSurchargePercentage * 100) : undefined,
						}, {
							id: "financialInfo.bunkerSurcharge",
							title: intl.formatMessage({ id: "RECHARGE_BUNKER_SURCHARGE" }),
							render: (row: RechargeLineFromQuery) => row.financialInfo?.bunkerSurcharge ? DataFormat.price(row.financialInfo.bunkerSurcharge, row.cargo?.charter?.contract?.currency?.isoCode ?? undefined) : undefined,
						}, {
							id: "financialInfo.invoiceAmount",
							title: intl.formatMessage({ id: "RECHARGE_INVOICE_AMOUNT" }),
							render: (row: RechargeLineFromQuery) => row.financialInfo?.invoiceAmount ? DataFormat.price(row.financialInfo.invoiceAmount, row.cargo?.charter?.contract?.currency?.isoCode ?? undefined) : undefined,
						}, {
							id: "billingComment",
							title: intl.formatMessage({ id: "RECHARGE_COMMENT" }),
							render: (row: RechargeLineFromQuery) => (
								<RechargeBillingCommentStatus
									id={row.id}
									value={row.billingComment ?? null}
									refetch={refetch} />
							),
							width: 140,
						}, {
							id: "billingState",
							title: intl.formatMessage({ id: "RECHARGE_BILLING_STATE" }),
							render: (row: RechargeLineFromQuery) => (
								<RechargeBillingStateStatus
									id={row.id}
									value={row.billingState ?? null}
									refetch={refetch} />
							),
							width: 140,
						} ]} />
			</Card>

			<RechargeBillingStatusEditDialog
				open={dialog.open}
				onClose={() => setDialog({ open: false, cargoActionIds: [] })}
				cargoActionIds={dialog.cargoActionIds}
				refetch={() => refetch()} />
		</React.Fragment>
	);
};

const formatLumpsumAndPricePerMT = (lumpsum: number | undefined, pricePerMT : number | undefined, currency: string | undefined) => {
	if (lumpsum && !pricePerMT) {
		return DataFormat.price(lumpsum, currency);
	}

	if (pricePerMT && !lumpsum) {
		return `${DataFormat.price(pricePerMT, currency)}/${DataFormat.UNITS.MTON}`;
	}

	if (lumpsum && pricePerMT) {
		return `${DataFormat.price(lumpsum, currency)} + ${DataFormat.price(pricePerMT, currency)}/${DataFormat.UNITS.MTON}`;
	}

	return undefined;
};

export default Recharge;
